import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErroApi, type Eu } from '../api';

export function useEu() {
  return useQuery<Eu | null>({
    queryKey: ['eu'],
    queryFn: async () => {
      try {
        // VA-R26-02: sem cookie de sessão nem chama /eu (evita o 401 no console das telas públicas).
        if (!(await api<{ autenticado: boolean }>('/api/v1/auth/sessao')).autenticado) return null;
        return await api<Eu>('/api/v1/auth/eu');
      } catch (e) {
        if (e instanceof ErroApi && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { email: string; senha: string }) => api<void>('/api/v1/auth/login', { method: 'POST', json: dados }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eu'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/v1/auth/logout', { method: 'POST' }),
    // Um refetch de ['eu'] em voo (ex.: o "Pular" do Tour) regravaria o Eu antigo por cima do null do logout.
    onMutate: () => qc.cancelQueries({ queryKey: ['eu'] }),
    onSettled: async () => {
      await qc.cancelQueries({ queryKey: ['eu'] });
      // Não usar qc.clear(): ele remove a query que o gate de sessão observa e a tela não volta ao login.
      qc.setQueryData(['eu'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'eu' });
    },
  });
}

export function useTrocarWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspace_id: string) => api<void>('/api/v1/auth/workspace', { method: 'POST', json: { workspace_id } }),
    onSuccess: () => qc.invalidateQueries(),
  });
}
